import { useEffect, useRef } from 'react'

// Both boxes this ring serves — the action row's integration tile and the
// reminder cell's checkbox — are 20px at `rounded-[6px]`. The ring sits 2px
// outside on every side, so its radius steps up to match: concentric rather
// than merely rounded, which is what keeps it reading as the box's own outline
// instead of a shape parked behind it.
const TILE_INSET = 2
const RING_STROKE = 1.5
const RING_BOX = 20 + TILE_INSET * 2
const RING_RADIUS = 6 + TILE_INSET

// The row is removed the instant its expiry window elapses, so a sweep timed to
// land on that same instant gets cut off a frame or two short — the last sliver
// never clears. Finishing marginally early is invisible; finishing late is the
// bug it was reported as.
const COMPLETION_LEAD_MS = 150

// The grey-to-green cross-fade as the action lands. Paired with the sweep in a
// two-value `transition-duration`, because both properties live on one element
// and one duration would drag the colour change out over the whole window.
const COLOR_FADE_MS = 200

/** `transition-duration` for [stroke-dashoffset, stroke], in that order. */
const durations = (sweepMs: number) => `${sweepMs}ms, ${COLOR_FADE_MS}ms`

/**
 * Sweep colour. `success` is an action that ran and landed; `neutral` is a
 * reminder ticked off, where nothing executed and green would overclaim.
 */
type RingTone = 'success' | 'neutral'

/**
 * Written out rather than interpolated — Tailwind only sees whole class names.
 *
 * `neutral` is grey in both themes, off `--foreground` rather than a fixed hex:
 * one literal grey can only be legible against one of the two surfaces, and this
 * ring is a hairline. Brand was the obvious alternative and is the wrong tool —
 * it's a fill colour, and the checkbox the ring wraps is already wearing it.
 */
const TONE_CLASS: Record<RingTone, { arc: string; track: string }> = {
  success: { arc: 'stroke-success', track: 'stroke-success/15' },
  neutral: { arc: 'stroke-foreground-secondary', track: 'stroke-foreground-disabled' },
}

interface ActionStatusRingProps {
  /**
   * Present once the action has landed: the ring turns `tone` and empties across
   * the row's remaining lifetime. Absent while the action is still running,
   * where the ring is a closed grey outline instead.
   */
  expiry?: { duration: number; startTime: string | number | Date }
  tone?: RingTone
}

/**
 * A status ring around the action's integration tile, spanning both halves of
 * executing: a closed grey outline while the action runs, then green and
 * emptying once it lands.
 *
 * One element across both phases, deliberately. The grey ring is what makes the
 * green one legible — on its own the countdown appeared already part-spent,
 * because the clock it runs on starts when the action executes rather than when
 * the result becomes visible. Showing the ring through the invoke means the
 * sweep is watched from full, and no timestamp has to be back-dated to fake it.
 *
 * Also the reminder cell's completion countdown (`reminders-cell.tsx`): a ticked
 * checkbox is the same 20px `rounded-[6px]` box as this tile and its undo window
 * is the same fact, so it takes the ring as-is rather than growing a second one.
 *
 * Around the tile rather than on the success badge: the badge's job is to say
 * the action landed, and a sweep drawn through it made one 14px mark carry two
 * unrelated facts. The tile is the biggest thing in the row that isn't text, so
 * it can hold a timer without competing with the title.
 *
 * Traces the tile's rounded square rather than circling it. A circle around a
 * squircle leaves four corner gaps that read as a mistake, and `pathLength`
 * normalises the rounded-rect perimeter to 100 so the sweep doesn't need the
 * arc-length arithmetic that shape would otherwise demand.
 *
 * Transitions `stroke-dashoffset`, which is paint-only rather than composited.
 * Deliberate: the alternatives (a conic-gradient behind `@property`, or a
 * two-wedge rotation mask) cost more DOM than one 24px outline is worth, and
 * this repaints a single path with no layout behind it.
 */
export function ActionStatusRing({ expiry, tone = 'success' }: ActionStatusRingProps) {
  const arcRef = useRef<SVGRectElement>(null)
  const duration = expiry?.duration
  const startTime = expiry?.startTime

  useEffect(() => {
    const el = arcRef.current
    if (!el) return

    // Still running: hold the outline closed. Reset explicitly rather than
    // trusting the initial value — this element survives the switch back out of
    // the expiring phase, which is what an undo does.
    if (duration == null || startTime == null) {
      el.style.transitionDuration = durations(0)
      el.style.strokeDashoffset = '0'
      return
    }

    const startMs = new Date(startTime).getTime()
    const endMs = startMs + duration - COMPLETION_LEAD_MS
    // Offset runs 0 (whole outline drawn) → 100 (nothing drawn), per pathLength.
    const offsetFor = (progress: number) => 100 * Math.min(Math.max(progress, 0), 1)

    if (Date.now() >= endMs) {
      el.style.strokeDashoffset = '100'
      return
    }

    el.style.transitionDuration = durations(0)
    el.style.strokeDashoffset = `${offsetFor((Date.now() - startMs) / duration)}`

    // Paint the seeked position before starting the sweep, on a later frame so
    // the browser doesn't coalesce both writes into one style recalc and jump
    // straight to the end. Two frames rather than one because a single rAF
    // still lands inside the same style pass in Chromium.
    //
    // The remaining time is measured inside the wait rather than before it for
    // the same reason: computed up front, the sweep ends two frames past the
    // row's removal, every time.
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        el.style.transitionDuration = durations(Math.max(endMs - Date.now(), 0))
        el.style.strokeDashoffset = '100'
      })
    })

    return () => {
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
    }
  }, [duration, startTime])

  const isExpiring = duration != null && startTime != null
  const { arc, track } = TONE_CLASS[tone]
  const geometry = {
    x: RING_STROKE / 2,
    y: RING_STROKE / 2,
    width: RING_BOX - RING_STROKE,
    height: RING_BOX - RING_STROKE,
    rx: RING_RADIUS,
    fill: 'none',
    strokeWidth: RING_STROKE,
  }

  return (
    // Sized by its own insets rather than centred with a translate: the wrapper
    // is whatever the box measures, and `-inset-0.5` stays concentric with it by
    // construction instead of depending on that measurement being exactly what
    // this file assumed. `size-auto` clears the UA's default svg sizing so the
    // insets are what determine the box.
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${RING_BOX} ${RING_BOX}`}
      className="pointer-events-none absolute -inset-0.5 size-auto"
    >
      {/* Only while emptying: it exists to show how much has gone. While the
          action runs the arc is the whole outline, and a track behind it would
          just double the stroke. */}
      {isExpiring && <rect {...geometry} className={track} />}
      <rect
        ref={arcRef}
        {...geometry}
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={100}
        // The pulse is the only thing saying the action is *doing* something —
        // a static outline around a static row reads as stalled. It stops dead
        // the moment the ring turns green, which is the point: the countdown is
        // a fact, not activity.
        className={isExpiring ? arc : 'stroke-foreground-disabled animate-pulse motion-reduce:animate-none'}
        // Durations are set imperatively above, per property — see `durations`.
        style={{ transitionProperty: 'stroke-dashoffset, stroke', transitionTimingFunction: 'linear' }}
      />
    </svg>
  )
}
